import { addToast, Button, DisplayInfo, Table } from '@octano/global-ui';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { useActionKitImp } from '../../../../../../../components/ozmoit/common';
import { SectionTitle } from '../../../../../../../components/text';
import TableEmptyContent from '../../../../../../../components/text/TableEmptyContent';
import { TranslationsKeys } from '../../../../../../../locales/translations';
import { SectionSchedule } from '../../../../../../../types/sectionMaintainerTypes';
import { formatDate } from '../../../../../../../utils/dates';
import { ScheduleSectionFormData } from '../../../../../../SectionsMaintainer/parts/ModalMaintainerSchedulePre';
import { ScheduleList } from '../../../../../../SectionsMaintainer/parts/SectionForm';
import { useColumnsSectionSchedule } from '../../../../../../SectionsMaintainer/parts/useColumnsSectionSchedule';
import { SectionScheduleBody } from '../../../../../../SectionsMaintainer/types';
import { formatModuleLabel } from '../../../../../../SectionsMaintainer/utils';
import { updateNonStructuredSectionSchedulesAction } from '../../../../../SectionsEdit/actions';
import { NonStructuredSectionSchedulesResponse } from '../../../../../SectionsEdit/types';
import ModalMaintainerSchedulePre from './ModalMaintainerSchedulePre';

type SectionScheduleFormProps = {
  data: NonStructuredSectionSchedulesResponse;
  onSave: () => void;
  isReadOnly?: boolean;
};

const SectionScheduleForm = ({
  data,
  onSave,
  isReadOnly,
}: SectionScheduleFormProps) => {
  const { t } = useTranslation();
  const { t: tCe } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );
  const prefix = 'sectionsMaintainer.update';
  const section = data.section;
  const isScheduleRequired = data.isScheduleRequired;

  const { action: updateSchedules } = useActionKitImp(
    updateNonStructuredSectionSchedulesAction,
    {
      onSuccess: () => {
        addToast({
          icon: 'success',
          color: 'success',
          text: t('common.messages.editSuccess'),
        });
        onSave();
      },
      onError: {
        unknown: () => {
          addToast({
            icon: 'error',
            color: 'danger',
            text: t(`common.errors.save`),
          });
        },
      },
    },
  );

  const setDefaultEndModule = (schedule: SectionSchedule) => {
    return {
      value: schedule.endModule?.id ?? schedule.startModule.id,
      label: formatModuleLabel(
        schedule.endModule ?? schedule.startModule,
        'endTime',
      ),
    };
  };

  const setDefaultDate = (date?: string | null) => {
    return date ? dayjs(date).toString() : undefined;
  };

  const [schedulesList, setSchedulesList] = useState<ScheduleList[]>(
    data.section.sectionSchedules.map((schedule: SectionSchedule) => {
      let campusOption = null;
      //si jornada es Presencial se toma el campus de la sala de clases
      if (schedule.classroom?.campus) {
        campusOption = {
          value: schedule.classroom.campus?.id,
          label: schedule.classroom.campus?.name,
        };
      }
      //si jornada es Online se toma el campus de la seccion
      if (data.section.campus) {
        campusOption = {
          value: data.section.campus?.id,
          label: data.section.campus?.name,
        };
      }

      return {
        id: schedule.id,
        isActive: !schedule.deletedAt,
        activity: {
          value: schedule.activity.id,
          label: schedule.activity.name,
        },
        modality: schedule.modality
          ? {
              value: schedule.modality.id,
              label: schedule.modality.name,
            }
          : null,
        campus: campusOption,
        classroom: schedule.classroom
          ? { value: schedule.classroom.id, label: schedule.classroom.name }
          : null,
        day: {
          value: schedule.day,
          label: t(`common.days.${schedule.day}`),
        },
        startModule: {
          value: schedule.startModule.id,
          label: formatModuleLabel(schedule.startModule, 'startTime'),
        },
        endModule: setDefaultEndModule(schedule),
        startDate: setDefaultDate(schedule.startDate),
        endDate: setDefaultDate(schedule.endDate),
        lessonsPeriodicity: schedule.lessonsPeriodicity
          ? {
              value: schedule.lessonsPeriodicity,
              label: schedule.lessonsPeriodicity,
            }
          : undefined,
        responsible: schedule.responsibles?.[0]
          ? {
              value: schedule.responsibles[0].id,
              label: schedule.responsibles[0].account?.fullName ?? '',
              account: schedule.responsibles[0].account,
            }
          : undefined,
      };
    }),
  );

  const onSubmit = useCallback(
    async (schedulesList: ScheduleList[]) => {
      const sectionSchedules: SectionScheduleBody[] = schedulesList
        .filter((schedule) => schedule.isActive)
        .map((schedule) => {
          let responsibleAccount;
          if (schedule.responsible?.account?.id) {
            responsibleAccount = {
              accountId: schedule.responsible?.account?.id,
            };
          } else if (schedule.responsible?.value) {
            responsibleAccount = {
              accountId: schedule.responsible?.value,
            };
          }
          return {
            id: schedule.id ?? null,
            day: schedule.day.value.toString(),
            startModuleId: +schedule.startModule.value,
            endModuleId: +schedule.endModule.value,
            classroomId: schedule.classroom?.value
              ? +schedule.classroom.value
              : undefined,
            activityId: +schedule.activity.value,
            modalityId: schedule.modality?.value
              ? +schedule.modality.value
              : undefined,
            responsibleAccount,
            startDate: schedule.startDate
              ? formatDate(schedule.startDate, 'YYYY-MM-DD')
              : '',
            endDate: schedule.endDate
              ? formatDate(schedule.endDate, 'YYYY-MM-DD')
              : '',
            lessonsPeriodicity: schedule.lessonsPeriodicity?.value as string,
          };
        });
      await updateSchedules(section.id, {
        sectionId: section.id,
        sectionSchedules,
      });
    },
    [section, updateSchedules],
  );

  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);

  const onDelete = useCallback(
    async (index: number) => {
      setDeletingIndex(index);
      schedulesList[index].isActive = false;
      const newSchedules = [...schedulesList];
      setSchedulesList(newSchedules);
      await onSubmit(newSchedules);
      setDeletingIndex(null);
    },
    [schedulesList, onSubmit],
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [scheduleIndex, setScheduleIndex] = useState<number>();

  const openCreateOrUpdate = (index?: number) => {
    setIsModalOpen(true);
    setScheduleIndex(index);
  };

  const columns = useColumnsSectionSchedule({
    openCreateOrUpdate,
    onDelete,
    deletingIndex,
    isReadOnly,
  });

  const onCreateOrUpdateSchedule = async (
    values: ScheduleSectionFormData,
    index?: number,
  ) => {
    const newSchedules = [...schedulesList];
    if (index !== undefined) {
      let updatedSchedule = { ...newSchedules[index] };
      updatedSchedule = {
        id: updatedSchedule.id,
        isActive: true,
        ...values,
        campus: values.classroom?.value ? values.campus : null,
      };
      newSchedules[index] = updatedSchedule;
    } else {
      newSchedules.push({
        id: null,
        isActive: true,
        ...values,
      });
    }
    setSchedulesList(newSchedules);
    await onSubmit(newSchedules);
    setIsModalOpen(false);
  };

  const scheduleToUpdate = useMemo(() => {
    return scheduleIndex !== undefined
      ? schedulesList?.[scheduleIndex]
      : undefined;
  }, [schedulesList, scheduleIndex]);

  return (
    <>
      <Row>
        <Col xs={{ size: 9 }} className="text-left">
          <SectionTitle text={section.name} />
        </Col>
        {!isReadOnly && (
          <Col xs={{ size: 3 }} className="text-right pb-3">
            <Button
              text={t(`${prefix}.addClass`)}
              size="md"
              icon="plus"
              fullwidth
              outlined
              disabled={!isScheduleRequired}
              onClick={() => {
                openCreateOrUpdate();
              }}
            />
          </Col>
        )}
        <Col xs={12}>
          {isScheduleRequired ? (
            <Table
              columns={columns}
              data={schedulesList}
              noResultsText={
                <TableEmptyContent
                  title={t(`${prefix}.noResultsClassesTitle`)}
                  subtitle={t(`${prefix}.noResultsClassesBody`)}
                />
              }
            />
          ) : (
            <div className="d-flex justify-content-center py-4">
              <DisplayInfo
                title={section.name}
                textBody={tCe('schedule.sectionAutoatendida')}
              />
            </div>
          )}
        </Col>
      </Row>
      {isModalOpen && (
        <ModalMaintainerSchedulePre
          sectionId={section.id}
          closeModal={() => setIsModalOpen(false)}
          onSave={onCreateOrUpdateSchedule}
          scheduleIndex={scheduleIndex}
          schedule={scheduleToUpdate}
          activities={data.activities}
          campuses={data.campuses}
          modules={data.timeModules}
          responsibles={data.teachers}
          modalities={data.modalities}
          periodicities={data.periodicities}
          period={section.period}
        />
      )}
    </>
  );
};

export default SectionScheduleForm;
